/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
*/

import React from 'react'
import R from 'ramda'
import autoBind from 'react-autobind'
import { injectIntl } from 'react-intl'

import { Card, MenuItem, CircularProgress, Subheader, RaisedButton  } from 'material-ui'
import { grey400 } from 'material-ui/styles/colors'
import { TextField, AutoComplete } from 'generic/form'

import * as constants from 'constants/Consumer'

import * as actions from 'actions/Consumer'
import * as identityActions from 'actions/Identity'
import * as entityActions from 'actions/Entity'

class Form extends React.Component {
  constructor(props) {
    super(props)

    autoBind(this)

    this.state = {
      loading: false,
      consumer: undefined,
      identity: undefined,
      entity: undefined,
      searchEntity: undefined,
      errors: {}
    }
  }

  componentDidMount() {
    this.setState({
      loading: true
    })

    const consumerUid = this.props.prestation.consumers.length ? this.props.prestation.consumers[0].uid : undefined
    let consumer = this.props.prestation.consumers.length ? this.props.prestation.consumers[0] : undefined
    let identity = consumer ? consumer.identities[0] : undefined
    let entity = consumer ? consumer.entities[0] : undefined

    actions.getConsumer(consumerUid)
      .then((result) => {
        consumer = {
          ...result,
          ...consumer,
        }

        this.setState({
          consumer: consumer
        })

        const identityUid = result.identities.length ? result.identities[0].uid : undefined

        return identityActions.getIdentity(identityUid)
      })
      .then((result) => {
        this.setState({
          identity: {
            ...result,
            ...identity,
            phone_numbers: identity && identity.phone_numbers ? [identity.phone_numbers] : [],
            emails: identity && identity.emails ? [identity.emails] : [],
          }
        })

        const entityUid = consumer.entities.length && consumer.entities[0] ? consumer.entities[0].uid : undefined

        return entityActions.getEntity(entityUid)
      })
      .then((result) => {
        this.setState({
          entity: {
            ...result,
            ...entity
          },
          searchEntity: result.name,
          loading: false
        })
      })
  }

  getFieldErrorObject (errors) {
    let errorObject = {}
    errors.forEach((error) => {
      errorObject[error.field_name] = error.errors[0].user_message
    })

    return errorObject
  }

  handleChange(name, value) {
    this.setState({
      [name]: value
    })
  }

  handleSearchChange(searchText) {
    this.handleChange('searchEntity', searchText)
    if (this.state.entity && searchText.length === 0) {
      this.setState({
        entity: undefined
      })
    }
  }

  handleEntityChange(name, value) {
    this.handleChange('entity', value)
  }

  handleIdentityChange(event, value) {
    const identity = {
      ...this.state.identity,
      [event.target.name]: value
    }

    this.handleChange('identity', identity)
  }

  handleContactInformationChange(event, value) {
    const identity = {
      ...this.state.identity,
      [event.target.name]: [value]
    }
    this.handleChange('identity', identity)
  }

  handleTransformEntity(entity) {
    return {
      ...entity,
      display: entity.name
    }
  }

  handleCancel() {
    this.props.handleFormChange('consumerFormVisible', false)
  }

  handleSave() {
    this.setState({
      loading: true
    })
    
    const payingCustomerAbaCode = R.path(['prestation', 'paying_customer', 'aba_code'], this.props)
    const emails = this.state.identity.emails.join()
    const phoneNumbers = this.state.identity.phone_numbers.join()

    const isEmailMandatory = this.props.prestation.paying_customer.mandatory_fields.includes('consumers.emails')
    const isPhoneNumberMandatory = this.props.prestation.paying_customer.mandatory_fields.includes('consumers.phone_number')

    if (
      !this.state.identity.first_name ||
      !this.state.identity.last_name ||
      (!emails && isEmailMandatory) ||
      (!phoneNumbers && isPhoneNumberMandatory)
    ) {
      this.setState({
        loading: false,
        errors: {
          first_name: !this.state.identity.first_name 
            ? this.props.intl.formatMessage(constants.messages.MANDATORY_FIELD) 
            : undefined,
          last_name: !this.state.identity.last_name 
            ? this.props.intl.formatMessage(constants.messages.MANDATORY_FIELD) 
            : undefined,
          emails: !emails && isEmailMandatory
            ? this.props.intl.formatMessage(constants.messages.MANDATORY_FIELD) 
            : undefined,
          phone_numbers: !phoneNumbers && isPhoneNumberMandatory
            ? this.props.intl.formatMessage(constants.messages.MANDATORY_FIELD) 
            : undefined,
        }
      })

      return
    }

    const identity = {
      ...this.state.identity,
      phone_numbers: phoneNumbers,
      emails: emails,
      id: this.state.identity.uid
        ? this.state.identity.id
        : undefined
    }

    identityActions.postIdentity(identity)
      .then((identity) => {
        const consumer = {
          ...this.state.consumer,
          identities: [identity],
          entities: this.state.entity ? [this.state.entity] : []
        }
        return actions.postConsumer(consumer)
      })
      .then((consumer) => {
        this.props.handlePrestationChange('consumers', [consumer])
        this.props.handleFormChange('consumerFormVisible', false)
      })
      .catch((errors) => {
        let fieldErrors = {}
        if (errors.response && errors.response.status === 400) {
          fieldErrors = this.getFieldErrorObject(errors.response.data.field_errors)
        }

        this.setState({
          loading: false,
          errors: fieldErrors
        })
      })
      .finally(() => {
        this.setState({
          loading: false
        })
      })
  }

  handleUnknownEntity() {
    const params = {
      search: 'name:' + constants.UNKNOWN_ENTITY_NAME
    }

    entityActions.getEntities(params)
      .then((results) => {
        this.setState({
          entity: results[0],
          searchEntity: results[0].name
        })
      })
  }

  render () {
    const identityMeta = this.state.identity ? this.state.identity.meta.fields : {}
    const payingCustomerAbaCode = R.path(['prestation', 'paying_customer', 'aba_code'], this.props)
    
    const isEmailMandatory = this.props.prestation.paying_customer.mandatory_fields.includes('consumers.emails')
    const isPhoneNumberMandatory = this.props.prestation.paying_customer.mandatory_fields.includes('consumers.phone_number')

    return (
      <Card
        style={{
          paddingLeft: 5,
          paddingRight: 5,
          paddingBottom: 10,
          height: '100%',
          ...this.props.style
        }}
      >
        {
          this.state.loading ||
          !this.state.consumer ||
          !this.state.identity
            ? <CircularProgress
              size={200}
              style={{
                width: '100%',
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
            : <React.Fragment>
              <Subheader
                style={styles.subHeader}
              >
                {this.props.intl.formatMessage(constants.messages.CREATE_CONSUMER)}
              </Subheader>

              <div
                style={!this.props.style ? {minHeight: 470} : {minHeight: 250}}
              >
                <div className='row'>
                  <div className='col-md-6 col-xs-12'>
                    <TextField
                      meta={identityMeta.first_name}
                      name='first_name'
                      value={this.state.identity.first_name}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.FIRST_NAME_LABEL)}
                      onChange={this.handleIdentityChange}
                      errorText={this.state.errors.first_name}
                      placeholderMandatory={true}
                    />
                  </div>

                  <div className='col-md-6 col-xs-12'>
                    <TextField
                      meta={identityMeta.last_name}
                      name='last_name'
                      value={this.state.identity.last_name}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.LAST_NAME_LABEL)}
                      onChange={this.handleIdentityChange}
                      errorText={this.state.errors.last_name}
                      placeholderMandatory={true}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6 col-xs-12'>
                    <TextField
                      meta={identityMeta.function}
                      name='function'
                      value={this.state.identity.function}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.FUNCTION_LABEL)}
                      onChange={this.handleIdentityChange}
                      errorText={this.state.errors.function}
                    />
                  </div>

                  <div className='col-md-6 col-xs-12'>
                    <AutoComplete
                      name='searchEntity'
                      textKey='display'
                      valueKey='uid'
                      searchKey='name'
                      popoverProps={{style: {width: 400}}}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.ENTITY_LABEL)}
                      url={entityActions.ENTITY_LIST_URL}
                      transformData={this.handleTransformEntity}
                      updateItem={this.handleEntityChange}
                      searchText={this.state.searchEntity}
                      onUpdateInput={this.handleSearchChange}
                      extraAction={this.handleUnknownEntity}
                      errorText={this.state.errors.entity}
                      placeholder={this.props.intl.formatMessage(constants.messages.CONSUMER_PLACEHOLDER)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6 col-xs-12'>
                    <TextField
                      meta={{'mandatory': isPhoneNumberMandatory}}
                      name='phone_numbers'
                      value={R.path(['phone_numbers', 0], this.state.identity)}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.PHONE_NUMBER_LABEL)}
                      onChange={this.handleContactInformationChange}
                      errorText={this.state.errors.phone_numbers}
                    />
                  </div>

                  <div className='col-md-6 col-xs-12'>
                    <TextField
                      meta={{'mandatory': isEmailMandatory}}
                      name='emails'
                      value={R.path(['emails', 0], this.state.identity)}
                      floatingLabelText={this.props.intl.formatMessage(constants.messages.EMAIL_LABEL)}
                      onChange={this.handleContactInformationChange}
                      errorText={this.state.errors.emails}
                    />
                  </div>
                </div>
              </div>

              <div
                className='row'
              >
                <div className='col-xs-offset-1 col-xs-5'>
                  <RaisedButton
                    style={styles.button}
                    label={this.props.intl.formatMessage(constants.messages.CONSUMER_ACTION_CANCEL)}
                    onClick={this.handleCancel}
                  />
                </div>

                <div className='col-xs-5'>
                  <RaisedButton
                    primary={true}
                    style={styles.button}
                    label={this.props.intl.formatMessage(constants.messages.CONSUMER_ACTION_SUBMIT)}
                    onClick={this.handleSave}
                  />
                </div>
              </div>
            </React.Fragment>
        }
      </Card>
    )
  }
}

const styles = {
  card: {
    marginTop: 5,
    paddingLeft: 10,
    paddingRight: 10,
    paddingBottom: 5,
    height: 600
  },
  subHeader: {
    textAlign: 'center'
  },
  button: {
    width: '100%',
    minWidth: '40%'
  },
}

export default injectIntl(Form)



// WEBPACK FOOTER //
// ./src/components/common/consumer/Form.js